import React from 'react';
import { Form, Input, InputNumber, Radio, Checkbox, Row, Col } from 'antd';
import type { FormInstance } from 'antd';

import LookupField from '../../../components/LookupField';
import { lookupApi } from '../../../services/lookupApi';

export interface LookupNames {
  department: string;
  customer: string;
  employee: string;
  purchaseReceipt: string;
  purchaseOrder: string;
  sampleAnalysis: string;
  remarkTemplate: string;
}

interface Props {
  form: FormInstance;
  disabled: boolean;
  lookupNames: LookupNames;
  onLookupNameChange: (key: keyof LookupNames, name: string) => void;
}

const reportMethods = [
  { value: 1, label: '正式' },
  { value: 2, label: '傳真' },
  { value: 3, label: 'E-MAIL' },
];

const productCategories = [
  { value: 1, label: '成品' },
  { value: 2, label: '半成品' },
];

const submitCategories = [
  { value: 1, label: '外部件' },
  { value: 2, label: '內部件' },
  { value: 3, label: 'R部門件' },
];

const Tab1BasicInfo: React.FC<Props> = ({ form, disabled, lookupNames, onLookupNameChange }) => {
  const resolveName = async (lookupType: string, nameKey: keyof LookupNames, code: string) => {
    if (!code.trim()) { onLookupNameChange(nameKey, ''); return; }
    try {
      const r = await lookupApi.getName(lookupType, code);
      onLookupNameChange(nameKey, r.name || '');
    } catch {
      onLookupNameChange(nameKey, '*查無此代號*');
    }
  };
  return (
    <div style={{ padding: '16px 0' }}>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="送件類別" name="fp210_35" rules={[{ required: true, message: '送件類別不可空白' }]}>
            <Radio.Group disabled={disabled}>
              {submitCategories.map(o => <Radio key={o.value} value={o.value}>{o.label}</Radio>)}
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="部門代號" name="fp210_12" rules={[{ required: true, message: '部門代號不可空白' }]}>
            <LookupField
              lookupType="department"
              value={form.getFieldValue('fp210_12') || ''}
              onChange={v => form.setFieldsValue({ fp210_12: v })}
              onCodeConfirm={code => resolveName('department', 'department', code)}
              disabled={disabled}
              maxLength={8} contractFieldId="fp210_12"
            />
          </Form.Item>
          {lookupNames.department && <span className="lookup-name">{lookupNames.department}</span>}
        </Col>
        <Col span={8}>
          <Form.Item label="申請人" name="fp210_13">
            <Input disabled={disabled} maxLength={10} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="業務人員" name="fp210_31" rules={[{ required: true, message: '業務人員不可空白' }]}>
            <LookupField
              lookupType="employee"
              value={form.getFieldValue('fp210_31') || ''}
              onChange={v => form.setFieldsValue({ fp210_31: v })}
              onCodeConfirm={code => resolveName('employee', 'employee', code)}
              disabled={disabled}
              maxLength={10} contractFieldId="fp210_31"
            />
          </Form.Item>
          {lookupNames.employee && <span className="lookup-name">{lookupNames.employee}</span>}
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="出報告方式" name="fp210_17" rules={[{ required: true, message: '出報告方式不可空白' }]}>
            <Radio.Group disabled={disabled}>
              {reportMethods.map(o => <Radio key={o.value} value={o.value}>{o.label}</Radio>)}
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="報告種類" name="fp210_171">
            <Radio.Group disabled={disabled}>
              <Radio value={1}>中文</Radio>
              <Radio value={2}>英文</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="產品類別" name="fp210_18">
            <Radio.Group disabled={disabled}>
              {productCategories.map(o => <Radio key={o.value} value={o.value}>{o.label}</Radio>)}
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="繳庫/託工入庫單號" name="fp210_14">
            <LookupField
              lookupType="purchaseReceipt"
              value={form.getFieldValue('fp210_14') || ''}
              onChange={v => form.setFieldsValue({ fp210_14: v })}
              onCodeConfirm={code => resolveName('purchaseReceipt', 'purchaseReceipt', code)}
              disabled={disabled}
              maxLength={8} contractFieldId="fp210_14"
            />
          </Form.Item>
          {lookupNames.purchaseReceipt && <span className="lookup-name">{lookupNames.purchaseReceipt}</span>}
        </Col>
        <Col span={8}>
          <Form.Item label="採購/繳庫項次" name="fp210_15">
            <InputNumber disabled={disabled} min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="採購/託工單號" name="fp210_30">
            <LookupField
              lookupType="purchaseOrder"
              value={form.getFieldValue('fp210_30') || ''}
              onChange={v => form.setFieldsValue({ fp210_30: v })}
              onCodeConfirm={code => resolveName('purchaseOrder', 'purchaseOrder', code)}
              disabled={disabled}
              maxLength={8} contractFieldId="fp210_30"
            />
          </Form.Item>
          {lookupNames.purchaseOrder && <span className="lookup-name">{lookupNames.purchaseOrder}</span>}
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="客戶代號" name="fp210_36">
            <LookupField
              lookupType="customer"
              value={form.getFieldValue('fp210_36') || ''}
              onChange={v => form.setFieldsValue({ fp210_36: v })}
              onCodeConfirm={code => resolveName('customer', 'customer', code)}
              disabled={disabled}
              maxLength={12} contractFieldId="fp210_36"
            />
          </Form.Item>
          {lookupNames.customer && <span className="lookup-name">{lookupNames.customer}</span>}
        </Col>
        <Col span={8}>
          <Form.Item label="客戶來樣分析單" name="fp210_32">
            <LookupField
              lookupType="sampleAnalysis"
              value={form.getFieldValue('fp210_32') || ''}
              onChange={v => form.setFieldsValue({ fp210_32: v })}
              onCodeConfirm={code => resolveName('sampleAnalysis', 'sampleAnalysis', code)}
              disabled={disabled}
              maxLength={8} contractFieldId="fp210_32"
            />
          </Form.Item>
          {lookupNames.sampleAnalysis && <span className="lookup-name">{lookupNames.sampleAnalysis}</span>}
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="不收費" name="fp210_33" valuePropName="checked">
            <Checkbox disabled={disabled} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="印制系統報告" name="fp210_34" valuePropName="checked">
            <Checkbox disabled={disabled} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="核准狀態" name="fp210_80">
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label="產品全名" name="fp210_38">
            <Input.TextArea disabled={disabled} rows={2} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default Tab1BasicInfo;
